import { useEffect, useState, useTransition, startTransition } from 'react';

interface UseDeferredInitOptions {
  delay?: number;
  priority?: 'immediate' | 'idle' | 'transition';
}

/**
 * Modern React 18 hook for deferred initialization
 * Uses concurrent features for non-blocking initialization
 */
export const useDeferredInit = (
  initFunction: () => void | Promise<void>,
  dependencies: React.DependencyList = [],
  options: UseDeferredInitOptions = {}
) => {
  const { delay = 0, priority = 'transition' } = options;
  const [isInitialized, setIsInitialized] = useState(false);
  const [isPending, startDeferredTransition] = useTransition();

  useEffect(() => {
    const performInit = async () => {
      try {
        await initFunction();
        setIsInitialized(true);
      } catch (error) {
        // Initialization failed - continue silently
      }
    };

    switch (priority) {
      case 'immediate':
        // Execute immediately
        performInit();
        break;

      case 'idle':
        // Execute when browser is idle
        if ('requestIdleCallback' in window) {
          const id = requestIdleCallback(() => {
            startTransition(() => {
              performInit();
            });
          });
          return () => cancelIdleCallback(id);
        } else {
          // Fallback to setTimeout
          const timer = setTimeout(() => {
            startTransition(() => {
              performInit();
            });
          }, delay);
          return () => clearTimeout(timer);
        }

      case 'transition':
      default:
        // Use React 18 transition for non-blocking execution
        if (delay > 0) {
          const timer = setTimeout(() => {
            startDeferredTransition(() => {
              performInit();
            });
          }, delay);
          return () => clearTimeout(timer);
        } else {
          startDeferredTransition(() => {
            performInit();
          });
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return {
    isInitialized,
    isPending,
  };
};